






























import { Component, Vue, Prop } from 'vue-property-decorator';

import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import CheckboxLegend from '@/componentsV2/molecules/Chart/CheckboxLegend.vue';
import BaseChart from '@/componentsV2/molecules/Chart/BaseChart.vue';
import { AnalyticsCardListItem } from '@/componentsV2/organisms/AnalyticsCardList/AnalyticsCardList.vue';

import PerformanceActivitiesService from '@/api/PerformanceActivitiesService';

import { IPeriod } from '@/models/period';
import { IChartSerie } from '@/models/performance';

import PerformanceUtils, {
  DatePickerShortcut,
  EChartTooltipCounterFormatNumber
} from '@/utils/PerformanceUtils';
import { EAnalyticsCardArrowDirection } from '@/componentsV2/molecules/AnalyticsCard/AnalyticsCard.vue';
import flagKeys from '@/constants/flagKeys';

const analyticsMapperBySlug: Record<string, any> = {
  'customer-spend': {
    key: 'customerSpend',
    'returning-customers': {
      key: 'returningCustomers'
    }
  }
};

@Component({
  components: {
    SectionData,
    CheckboxLegend,
    BaseChart
  }
})
export default class PerformanceActivitiesTotalSpendCustomers extends Vue {
  @Prop() gaEventPage!: string;

  performanceActivitiesService = new PerformanceActivitiesService();
  period: IPeriod = PerformanceUtils.periodOfThirtyDays();
  options = PerformanceUtils.chartOptions({
    colors: ['#3E24D6'],
    prefixTooltipLegend: `${this.currencySymbol}`,
    tooltipCounterFormatNumber: EChartTooltipCounterFormatNumber.TREAT_VALUE
  });
  series: IChartSerie[] = [];
  loading = false;

  analytics: Record<string, AnalyticsCardListItem> = {
    customerSpend: {
      insightVariant: 'normal',
      infos: {
        amount: '',
        counterFractionDigits: 2,
        description: '',
        prefixAmount: '',
        tooltipText: ''
      }
    }
  };

  get legendGATracking() {
    return {
      eventCategory: `${this.gaEventPage} |
        ${this.$t(`${this.tPrefix}.graphic.titleChart`)} - ${this.$t(
        `${this.tPrefix}.graphic.legendGATracking`
      )}`,
      eventAction: 'click'
    };
  }

  get gaTracking() {
    return {
      period: {
        eventCategory: `${this.gaEventPage} |
        ${this.$t(`${this.tPrefix}.graphic.titleChart`)} - ${this.$t(
          `${this.tPrefix}.graphic.periodGATracking`
        )}`,
        eventAction: 'change'
      }
    };
  }

  get translatedAnalytics() {
    return Object.entries(this.analytics)
      .map(([key, value]) => ({
        ...value,
        infos: {
          ...value.infos,
          description: this.$t(`${this.tPrefix}.cards.${key}.description`),
          tooltipText: this.$t(`${this.tPrefix}.cards.${key}.tooltipText`)
        }
      }))
      .filter(({ hidden }) => !hidden);
  }

  get queryParameters() {
    return {
      from: this.period.start,
      to: this.period.end,
      ...(this.period.shortcut && {
        slug:
          PerformanceUtils.datepickerShortcutMapper[
            this.period.shortcut as DatePickerShortcut
          ]
      })
    };
  }

  get tPrefix() {
    return `performance.activities.totalSpend.spendOverTime`;
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get currencySymbol() {
    return this.$store.getters['internationalization/getCountrySettings']
      .currencySymbol;
  }

  created() {
    this.getCustomerSpend();
  }

  async getCustomerSpend({ useFilters = true } = {}) {
    this.loading = true;

    const queryParameters = useFilters ? this.queryParameters : {};

    const response = await this.performanceActivitiesService.getCustomerSpend(
      queryParameters
    );

    this.loading = false;

    if (response.status !== 200) return;
    for (const dataset of response.data.datasets) {
      dataset.label = String(
        this.$t(`${this.tPrefix}.graphic.labelLegendGraphic`)
      );

      if (!analyticsMapperBySlug[dataset.slug]) continue;

      const slugKey = analyticsMapperBySlug[dataset.slug].key;

      const datasetPercentage = Number(dataset.percentage || 0);
      const roundedDatasetPercentage = Math.round(datasetPercentage);

      this.analytics = {
        ...this.analytics,
        [slugKey]: {
          ...this.analytics[slugKey],
          insightVariant:
            roundedDatasetPercentage > 0
              ? 'success'
              : roundedDatasetPercentage < 0
              ? 'danger'
              : 'normal',
          hidden: false,
          infos: {
            ...this.analytics[slugKey].infos,
            amount: dataset.total,
            prefixAmount: this.currencySymbol,
            arrowDirection:
              roundedDatasetPercentage > 0
                ? EAnalyticsCardArrowDirection.UP
                : roundedDatasetPercentage < 0
                ? EAnalyticsCardArrowDirection.DOWN
                : undefined,
            insightText: this.period.shortcut
              ? `${roundedDatasetPercentage}% ${PerformanceUtils.getComparativeLabel(
                  this.period.shortcut as DatePickerShortcut
                )}`
              : ''
          }
        }
      };
    }

    this.series = PerformanceUtils.normalizedChartSeries(response.data);
  }

  handlePeriodChange(period: IPeriod) {
    this.period = PerformanceUtils.normalizedPeriod(period);
    this.getCustomerSpend();
  }
}
